import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { BadgeTypeConstants, ColorConstants, ProjectIssuanceConstants } from "../../../../../constants";
import { BadgeData, Metric, ObjectType } from "../../../../../models";
import {
  GetProjectDetailsResponse,
  getProjectIssuances,
  getStandardSpecificSchemaDetails,
} from "../../../../../service/publicquery";
import { Status } from "../../../../../service/Shared";
import { saveEvent } from "../../../../../service/stats";
import { jsonReviver } from "../../../../../utils";
import { StackedChartData } from "../../../../../widget";
import { StandardSpecificDefinition } from "../../../../shared/projects/project/overview/components/StandardSpecificSection/types";
import { usePublicContext } from "../../../usePublicContext";

interface UseOverviewReturnData {
  isLoading: boolean;
  projectDetails: GetProjectDetailsResponse | undefined;
  tags: string[] | null;
  badges: BadgeData[];
  certifications: BadgeData[];
  stackedChartData: StackedChartData[] | undefined;
  xAxisLabels: string[];
  standardSpecificDefinition: StandardSpecificDefinition | undefined;
}

export const useOverview = (): UseOverviewReturnData => {
  const { uuid } = useParams();
  const { projectDetails } = usePublicContext();
  const tags = !!projectDetails?.tags && !!projectDetails?.tags.length ? projectDetails?.tags : null;
  const certifications =
    projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_CERTIFICATION) || [];
  const badges = projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_BADGE) || [];

  const [isLoading, setIsLoading] = useState(true);
  const [standardSpecificDefinition, setStandardSpecificDefinition] = useState<
    StandardSpecificDefinition | undefined
  >();

  const [stackedChartData, setStackedChartData] = useState<StackedChartData[]>([]);
  const [xAxisLabels, setXAxisLabels] = useState<string[]>([]);

  const fetchData = useCallback(async (projectUuid: string, standardSpecificSchemaUuid: string) => {
    const [projectIssuancesResponse, standardSpecificDetailsResponse] = await Promise.all([
      getProjectIssuances({
        projectUuid,
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 100,
        },
      }),
      getStandardSpecificSchemaDetails({
        standardSpecificSchemaUuid,
      }),
    ]);
    if (
      projectIssuancesResponse.status === Status.Success &&
      projectIssuancesResponse.data &&
      projectIssuancesResponse.data.results.length > 0 &&
      projectDetails?.unitMeasurements[0] === "Carbon"
    ) {
      const keys: string[] = [];
      const verifiedUnits: number[] = [];
      const pendingUnits: number[] = [];

      const results = projectIssuancesResponse.data?.results ?? [];

      results.forEach((value) => {
        const endYear = JSON.parse(value.standardSpecificIssuanceData, jsonReviver)?.vintageEndDate?.getFullYear();
        const period = endYear?.toString();

        // Avoid duplicate keys
        if (period && !keys.includes(period)) {
          const total = results
            .filter(
              (d) => JSON.parse(d.standardSpecificIssuanceData, jsonReviver)?.vintageEndDate?.getFullYear() <= endYear
            )
            .reduce((acc: number, k) => acc + k.unitQuantityTotal, 0);

          keys.push(period);
          verifiedUnits.push(total);
          pendingUnits.push(projectDetails?.cachedTotalQuantity ? projectDetails.cachedTotalQuantity - total : 0);
        }
      });

      const unitTypes = ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_BY_STANDARD[projectDetails.standard.uuid];

      setXAxisLabels(keys);
      if (keys && keys.length) {
        setStackedChartData([
          {
            label:
              ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_LONG_HAND[unitTypes[0]] ?? "Verified carbon units (VCUs)",
            data: verifiedUnits,
            backgroundColor: ColorConstants.INDIGO_500,
            hoverBackgroundColor: ColorConstants.INDIGO_400,
          },
          {
            label:
              ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_LONG_HAND[unitTypes[1]] ??
              "Pending issuance units (PIUs)",
            data: pendingUnits,
            backgroundColor: ColorConstants.INDIGO_200,
            hoverBackgroundColor: ColorConstants.INDIGO_100,
          },
        ]);
      }
    }

    if (standardSpecificDetailsResponse.status === Status.Success && standardSpecificDetailsResponse.data) {
      setStandardSpecificDefinition(standardSpecificDetailsResponse.data.definition);
    }
  }, []);

  useEffect(() => {
    if (uuid && projectDetails?.standardSpecificSchemaUuid) {
      fetchData(uuid, projectDetails?.standardSpecificSchemaUuid).finally(async () => {
        setIsLoading(false);
        saveEvent({
          metric: Metric.View,
          objectType: ObjectType.Project,
          objectUuid: uuid,
          timestamp: null,
        });
      });
    }
  }, [fetchData, uuid, projectDetails]);

  return {
    isLoading,
    stackedChartData,
    xAxisLabels,
    tags,
    badges,
    certifications,
    projectDetails,
    standardSpecificDefinition,
  };
};
